import { useState } from "react";
import { Link } from "react-router-dom";
import { CheckCircle2, Loader2 } from "lucide-react";
import { toast } from "sonner";
import { api, apiError } from "@/api";
import { useLang } from "@/i18n";

export const BespokeDialog = ({ open, onClose, mode, stops, prefs }) => {
    const { lang, t } = useLang();
    const [form, setForm] = useState({ customer_name: "", customer_email: "", customer_phone: "", privacy_consent: false });
    const [sending, setSending] = useState(false);
    const [result, setResult] = useState(null);
    if (!open) return null;

    const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));
    const valid = form.customer_name.length >= 2 && form.customer_email.includes("@") && form.customer_phone.length >= 5 && form.privacy_consent && stops.length > 0;

    const submit = async () => {
        setSending(true);
        const notesParts = [];
        if (prefs.transportCustom) notesParts.push(`TRASPORTO PERSONALIZZATO: gruppo di ${prefs.participants || "?"} persone oltre la capienza standard — richiesta soluzione dedicata`);
        if (prefs.notes) notesParts.push(prefs.notes);
        try {
            const { data } = await api.post("/public/builder/bespoke", {
                mode,
                stops: stops.map((s) => ({
                    id: s.id,
                    name: s.name,
                    nights: s.nights || null,
                    day: s.day || null,
                    kind: s.kind || null,
                    category: s.category || null,
                    duration_min: s.duration_min || null,
                    locality: s.locality || null,
                    region: s.region || null,
                    experiences: s.experiences || [],
                    attractions: (s.attractions || []).map((a) => ({ id: a.id, name: a.name, day: a.day || null })),
                    stay: s.stay ? {
                        name: s.stay.name, type: s.stay.type || null, price: s.stay.price ?? null, link: s.stay.link || null,
                        place_id: s.stay.place_id || null, address: s.stay.address || null,
                        rating: s.stay.rating ?? null, notes: s.stay.notes || null,
                        checkin: s.stay.checkin || null, checkout: s.stay.checkout || null,
                        guests: s.stay.guests || null, rooms: s.stay.rooms || null,
                        lat: s.stay.lat ?? null, lng: s.stay.lng ?? null,
                    } : null,
                })),
                date: prefs.date || prefs.startDate || null,
                end_date: prefs.endDate || null,
                days: mode === "rome" ? (Number(prefs.days) || null) : null,
                time: prefs.time || "09:00",
                participants: Number(prefs.participants) || 2,
                pace: prefs.pace || null,
                interests: prefs.interests || [],
                transport: prefs.transport || null,
                budget: prefs.budget ? `€ ${Number(prefs.budget).toLocaleString("it-IT")} (intero viaggio, indicativo)` : null,
                notes: notesParts.length ? notesParts.join("\n") : null,
                language: lang,
                ...form,
                origin_url: window.location.origin,
            });
            setResult(data);
        } catch (e) {
            toast.error(apiError(e, t.booking.errorGeneric));
        } finally {
            setSending(false);
        }
    };

    const close = () => { onClose(); setResult(null); };
    const L = ({ children }) => <span className="text-[11px] uppercase tracking-widest text-amber-500/80">{children}</span>;

    return (
        <div className="fixed inset-0 z-[80] flex items-center justify-center bg-black/70 backdrop-blur-sm px-4" data-testid="bespoke-dialog">
            <div className="w-full max-w-md rounded-2xl border border-amber-500/30 bg-[#151515] p-8 max-h-[90vh] overflow-y-auto">
                {result ? (
                    <div className="text-center py-6" data-testid="bespoke-success">
                        <CheckCircle2 className="mx-auto text-amber-400" size={52} />
                        <h3 className="mt-4 font-display text-2xl text-amber-50">{t.builder.bkSuccessTitle}</h3>
                        <p className="mt-3 text-sm text-stone-300 leading-relaxed">{t.builder.bkSuccessMsg}</p>
                        {result.code && (
                            <p className="mt-4 font-mono-accent text-xl text-amber-300" data-testid="bespoke-success-code">{result.code}</p>
                        )}
                        <div className="mt-6 flex flex-col items-center gap-3">
                            <Link to={`/stato-prenotazione?code=${result.code || ""}`} data-testid="bespoke-status-link" className="text-xs uppercase tracking-[0.14em] text-amber-300 underline underline-offset-4">
                                {t.nav.statusPage}
                            </Link>
                            <button data-testid="bespoke-close-button" onClick={close} className="rounded-full border border-amber-500/40 px-6 py-2 text-xs uppercase tracking-widest text-amber-200 hover:bg-amber-400 hover:text-black transition-colors">
                                {t.booking.close}
                            </button>
                        </div>
                    </div>
                ) : (
                    <>
                        <h3 className="font-display text-2xl text-amber-50">{t.builder.bkTitle}</h3>
                        <p className="mt-1 text-xs text-stone-400">{t.builder.bkSub}</p>
                        <div className="mt-5 rounded-lg border border-amber-500/20 bg-black/40 p-4 text-xs text-stone-300" data-testid="bespoke-summary">
                            {stops.map((s, i) => `${i + 1}. ${s.name}${s.nights ? ` (${s.nights}n)` : ""}`).join("  →  ")}
                        </div>
                        <div className="mt-5 grid gap-4">
                            <label className="block"><L>{t.booking.name} *</L><input data-testid="bespoke-name" className="input-lux mt-1" value={form.customer_name} onChange={(e) => set("customer_name", e.target.value)} /></label>
                            <label className="block"><L>{t.booking.email} *</L><input data-testid="bespoke-email" type="email" className="input-lux mt-1" value={form.customer_email} onChange={(e) => set("customer_email", e.target.value)} /></label>
                            <label className="block"><L>{t.booking.phone} *</L><input data-testid="bespoke-phone" type="tel" className="input-lux mt-1" value={form.customer_phone} onChange={(e) => set("customer_phone", e.target.value)} /></label>
                            <label className="flex items-start gap-3 text-xs text-stone-400 cursor-pointer">
                                <input data-testid="bespoke-privacy" type="checkbox" checked={form.privacy_consent} onChange={(e) => set("privacy_consent", e.target.checked)} className="mt-0.5 accent-amber-400" />
                                {t.booking.privacy}
                            </label>
                            <div className="flex gap-3">
                                <button data-testid="bespoke-submit" onClick={submit} disabled={!valid || sending}
                                    className="flex-1 inline-flex items-center justify-center gap-2 rounded-full bg-amber-400 px-6 py-3.5 text-xs font-bold uppercase tracking-[0.14em] text-black transition-all hover:bg-amber-300 disabled:opacity-40">
                                    {sending && <Loader2 size={14} className="animate-spin" />}
                                    {t.builder.bkSubmit}
                                </button>
                                <button data-testid="bespoke-cancel" onClick={close} className="rounded-full border border-stone-600 px-6 py-3.5 text-xs uppercase tracking-widest text-stone-300 hover:border-amber-500/50">
                                    {t.booking.close}
                                </button>
                            </div>
                        </div>
                    </>
                )}
            </div>
        </div>
    );
};
